import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { copilotApi } from '../services/api';
import './clinic.css';
import './CopilotStudio.css';

const starter = 'Ho un nuovo paziente Flavio Perrone di 56 anni che pesa 85 kg. È la prima visita e devo fare la BIA.';

const sexLabel = value => value === 'Male' ? 'Maschio' : value === 'Female' ? 'Femmina' : 'Da specificare';

export function SibillaCopilot() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialRequestRef = useRef(location.state?.autoSubmit ? location.state.initialMessage : null);
  const [message, setMessage] = useState(location.state?.initialMessage || '');
  const [history, setHistory] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState('');
  const planHeadingRef = useRef(null);

  const context = useMemo(() => {
    const lastStudio = history.map(item => !!item.studio).lastIndexOf(true);
    return history.slice(lastStudio + 1).filter(item => item.role === 'user').map(item => item.text).join('\n');
  }, [history]);

  const openExistingPatient = (match, selectedPlan = plan) => {
    if (selectedPlan?.intent && selectedPlan.intent !== 'CreateVisit') {
      navigate(`/patients/${match.id}`, {
        state: { openDietTab: true, copilotDietDraft: selectedPlan.dietDraftProposal || null }
      });
      return;
    }
    const visit = selectedPlan?.visit;
    navigate(`/patients/${match.id}/visits/new${visit?.requiresBia ? '?bia=1' : ''}`, {
      state: {
        copilotVisit: {
          type: visit?.isFirstVisit ? 'Prima visita' : 'Controllo',
          reason: visit?.reason || '',
          weightKg: visit?.weightKg ?? ''
        }
      }
    });
  };

  useEffect(() => {
    if (!plan || !planHeadingRef.current) return;
    planHeadingRef.current.focus({ preventScroll: true });
    if (window.matchMedia('(max-width: 980px)').matches) {
      planHeadingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [plan]);

  const submit = useCallback(async (event, requestedText) => {
    event?.preventDefault();
    const text = (requestedText ?? message).trim();
    if (!text || loading) return;
    setLoading(true);
    setError('');
    try {
      const nextPlan = await copilotApi.plan({ message: text, context: context || null });
      const timestamp = Date.now();
      setHistory(items => [...items,
        { id: `${timestamp}-user`, role: 'user', text },
        {
          id: `${timestamp}-assistant`,
          role: 'assistant',
          text: nextPlan.summary,
          matches: nextPlan.possibleMatches || [],
          visit: nextPlan.visit,
          intent: nextPlan.intent,
          studio: nextPlan.studio,
          dietDraftProposal: nextPlan.dietDraftProposal
        }
      ]);
      setPlan(nextPlan);
      setMessage('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [context, loading, message]);

  useEffect(() => {
    const initialMessage = initialRequestRef.current?.trim();
    if (!initialMessage) return;
    initialRequestRef.current = null;
    navigate(location.pathname, { replace: true, state: null });
    submit(null, initialMessage);
  }, [location.pathname, navigate, submit]);

  const execute = async () => {
    if (!plan?.canExecute || executing) return;
    setExecuting(true);
    setError('');
    try {
      const result = await copilotApi.execute({ planToken: plan.planToken, confirmed: true });
      navigate(`/patients/${result.patientId}/visits/${result.visitId}${result.requiresBia ? '?bia=1' : ''}`);
    } catch (requestError) {
      setError(`${requestError.message}. Il piano non è stato salvato.`);
      setExecuting(false);
    }
  };

  const useExample = () => {
    setMessage(starter);
    document.getElementById('copilotCommand')?.focus();
  };

  return <main className="clinic-page copilot-page">
    <header className="copilot-head">
      <div>
        <h1>Chiedi a Sibilla</h1>
        <p>Descrivi il lavoro clinico come lo diresti a una collega. Sibilla prepara le azioni; tu controlli e confermi.</p>
      </div>
      <div className="copilot-safety"><span aria-hidden="true" /><strong>Nessun salvataggio automatico</strong><small>Le modifiche richiedono conferma</small></div>
    </header>

    {error && <div className="copilot-error" role="alert"><div><strong>Non ho potuto preparare il piano</strong><span>{error}. Il testo è rimasto nel campo: puoi riprovare senza riscriverlo.</span></div><button type="button" onClick={() => submit()} disabled={loading}>{loading ? 'Riconnessione…' : 'Riprova ora'}</button></div>}

    <div className={`copilot-layout${plan ? ' has-plan' : ''}`}>
      <section className="copilot-conversation" aria-label="Conversazione con Sibilla">
        <div className="copilot-transcript" aria-live="polite">
          {!history.length && <div className="copilot-welcome">
            <span className="copilot-mark" aria-hidden="true">S</span>
            <h2>Da dove iniziamo?</h2>
            <p>Posso riepilogare le priorità dello studio, i piani da consegnare e fatture e TS aperti. Posso anche preparare visite e BIA, analizzare l’andamento di un paziente e proporre una bozza alimentare modificabile.</p>
            <button type="button" onClick={useExample}>Usa un esempio</button>
          </div>}
          {history.map(item => <div key={item.id} className={`copilot-message ${item.role}${item.matches?.length ? ' decision' : ''}`}>
            <span>{item.role === 'user' ? 'Tu' : 'Sibilla'}</span><p>{item.text}</p>
            {!!item.matches?.length && <div className="copilot-message-choices" aria-label="Scegli il paziente esistente">
              {item.matches.map(match => <button key={match.id} type="button" onClick={() => openExistingPatient(match, item)}>
                <strong>Usa {match.displayName}</strong>
                <small>{match.dateOfBirth ? `Nato/a il ${new Date(`${match.dateOfBirth}T00:00:00`).toLocaleDateString('it-IT')}` : 'Data di nascita non presente'} · {item.intent === 'CreateVisit' ? `apri la visita${item.visit?.requiresBia ? ' con BIA' : ''}` : 'apri andamento e bozza dieta'}</small>
              </button>)}
            </div>}
          </div>)}
          {loading && <div className="copilot-message assistant loading" role="status"><span>Sibilla</span><p>Sto verificando la richiesta e i dati disponibili…</p></div>}
        </div>
        <form className="copilot-composer" onSubmit={submit}>
          <label htmlFor="copilotCommand">La tua richiesta</label>
          <textarea id="copilotCommand" value={message} onChange={event => setMessage(event.target.value)} placeholder={plan?.missingFields?.length ? `Aggiungi: ${plan.missingFields.join(', ')}` : 'Es. Ho un nuovo paziente…'} rows="3" maxLength="4000" />
          <div><small>Inserisci solo i dati clinici necessari alla richiesta.</small><button type="submit" disabled={!message.trim() || loading}>{loading ? 'Analisi in corso' : plan ? 'Aggiorna piano' : 'Prepara piano'}</button></div>
        </form>
      </section>

      <aside className="copilot-plan" aria-label="Piano operativo proposto">
        <div className="copilot-plan-head"><div><h2 ref={planHeadingRef} tabIndex="-1">{plan?.studio ? 'Attività dello studio' : 'Piano operativo'}</h2><p>{plan?.studio ? 'Dati aggiornati · sola lettura' : plan ? 'Proposta da verificare' : 'In attesa della richiesta'}</p></div>{plan && <span>{plan.studio ? 'Studio' : plan.interpreter === 'OpenAI' ? 'IA' : 'Locale'}</span>}</div>
        {plan && !plan.studio && <p className="copilot-live-status" role="status">{plan.intent === 'CreateVisit' ? `Piano aggiornato. ${plan.missingFields?.length ? `Informazioni mancanti: ${plan.missingFields.join(', ')}.` : 'Tutti i dati obbligatori sono presenti.'}` : plan.intent === 'Unknown' ? 'Richiesta da chiarire.' : 'Richiesta riconosciuta: andamento clinico e piano alimentare.'} {plan.possibleMatches?.length ? `${plan.possibleMatches.length} ${plan.intent === 'CreateVisit' ? 'possibili duplicati' : 'cartelle corrispondenti'} trovate.` : ''}</p>}
        {!plan ? <div className="copilot-plan-empty"><span aria-hidden="true" /><p>Qui compariranno i dati riconosciuti, ciò che manca e le azioni proposte.</p></div> : plan.studio ? <StudioSummary studio={plan.studio} /> : <>
          {!!plan.possibleMatches?.length && <section className="copilot-match-decision">
            <h3>{plan.possibleMatches.length === 1 ? (plan.intent === 'CreateVisit' ? 'Paziente già presente' : 'Cartella analizzata') : 'Quale paziente vuoi usare?'}</h3>
            <p>{plan.possibleMatches.length === 1 ? (plan.intent === 'CreateVisit' ? 'Usa la cartella esistente per evitare un duplicato.' : 'Ho usato questa cartella per andamento e bozza alimentare.') : `Seleziona la cartella corretta prima di ${plan.intent === 'CreateVisit' ? 'aprire la visita' : 'analizzare i dati'}.`}</p>
            {plan.possibleMatches.map(match => <button key={match.id} type="button" onClick={() => openExistingPatient(match)}>
              <span><strong>{match.displayName}</strong><small>{match.dateOfBirth ? `Nato/a il ${new Date(`${match.dateOfBirth}T00:00:00`).toLocaleDateString('it-IT')}` : 'Data di nascita non presente'}</small></span>
              <b>{plan.intent === 'CreateVisit' ? 'Usa questa cartella' : 'Apri analisi e bozza'}</b>
            </button>)}
          </section>}

          {plan.intent === 'CreateVisit' ? <dl className="copilot-facts">
            <div><dt>Paziente</dt><dd>{[plan.patient.firstName, plan.patient.lastName].filter(Boolean).join(' ') || 'Non riconosciuto'}</dd></div>
            <div><dt>Età dichiarata</dt><dd>{plan.patient.age ? `${plan.patient.age} anni` : 'Non indicata'}</dd></div>
            <div><dt>Data di nascita</dt><dd>{plan.patient.dateOfBirth ? new Date(`${plan.patient.dateOfBirth}T00:00:00`).toLocaleDateString('it-IT') : 'Da completare'}</dd></div>
            <div><dt>Sesso biologico</dt><dd>{sexLabel(plan.patient.sex)}</dd></div>
            <div><dt>Altezza</dt><dd>{plan.patient.heightCm ? `${plan.patient.heightCm} cm` : 'Da completare'}</dd></div>
            <div><dt>Peso</dt><dd>{plan.visit.weightKg ? `${plan.visit.weightKg} kg` : 'Non indicato'}</dd></div>
          </dl> : <dl className="copilot-facts">
            <div><dt>Paziente</dt><dd>{[plan.patient.firstName, plan.patient.lastName].filter(Boolean).join(' ') || 'Non riconosciuto'}</dd></div>
            <div><dt>BIA disponibili</dt><dd>{plan.progress?.measurementCount ?? 0}</dd></div>
            <div><dt>Ultimo peso</dt><dd>{plan.progress?.latestWeightKg != null ? `${plan.progress.latestWeightKg.toFixed(1)} kg` : 'Non disponibile'}</dd></div>
            <div><dt>Variazione</dt><dd>{plan.progress?.weightChangeKg != null ? `${plan.progress.weightChangeKg > 0 ? '+' : ''}${plan.progress.weightChangeKg.toFixed(1)} kg` : 'Serve almeno un confronto'}</dd></div>
            <div><dt>Ultimo BMI</dt><dd>{plan.progress?.latestBmi != null ? plan.progress.latestBmi.toFixed(1) : 'Non disponibile'}</dd></div>
            <div><dt>Massa grassa</dt><dd>{plan.progress?.latestFatMassPercent != null ? `${plan.progress.latestFatMassPercent.toFixed(1)}%` : 'Non disponibile'}</dd></div>
          </dl>}

          {plan.dietDraftProposal && <section className="copilot-diet-preview"><h3>{plan.dietDraftProposal.draft.name}</h3><p>{plan.dietDraftProposal.draft.plan.length} proposte alimentari preparate. {plan.dietDraftProposal.draft.planningMethod === 'EnergyTarget' ? 'Quantità e obiettivo energetico restano da verificare.' : 'Porzioni, frequenze e alternative restano da verificare; non vengono conteggiate calorie.'}</p><strong>Bozza IA · non salvata</strong></section>}

          {!!plan.missingFields?.length && <section className="copilot-attention"><h3>Prima di procedere</h3><p>Scrivi nel messaggio successivo: {plan.missingFields.join(', ')}.</p></section>}

          <ol className="copilot-actions">{plan.proposedActions.map(action => <li key={action}><span aria-hidden="true" />{action}</li>)}</ol>
          {!!plan.warnings?.length && <details className="copilot-warnings"><summary>Controlli e avvertenze ({plan.warnings.length})</summary>{plan.warnings.map(warning => <p key={warning}>{warning}</p>)}</details>}

          {plan.intent === 'CreateVisit' ? <div className="copilot-confirm"><p>{plan.possibleMatches?.length ? 'Scegli qui sopra la cartella esistente da usare.' : plan.canExecute ? 'Controlla i dati: la conferma creerà paziente e visita.' : 'Completa i dati richiesti per abilitare la conferma.'}</p><button type="button" onClick={execute} disabled={!plan.canExecute || executing}>{executing ? 'Creazione in corso' : 'Conferma e apri visita'}</button></div> : <div className="copilot-confirm"><p>Nessun dato è stato salvato. Apri la cartella per controllare andamento e bozza.</p><button type="button" onClick={() => plan.possibleMatches?.length === 1 && openExistingPatient(plan.possibleMatches[0])} disabled={plan.possibleMatches?.length !== 1}>Apri cartella e rivedi bozza</button></div>}
        </>}
      </aside>
    </div>
  </main>;
}

function StudioSummary({ studio }) {
  return <div className="copilot-studio">
    <p className="copilot-studio-time">Aggiornato alle {new Date(studio.updatedAt).toLocaleTimeString('it-IT', { timeZone: 'Europe/Rome', hour: '2-digit', minute: '2-digit' })}</p>
    {studio.sections.map(section => <section key={section.title} className="copilot-studio-section">
      <header><h3>{section.title}</h3><strong>{section.count}</strong></header>
      {section.count === 0 ? <p>Nessuna attività aperta.</p> : <ul>{section.items.map((item, index) =>
        <li key={`${item.href}-${index}`}><Link to={item.href}><span><strong>{item.title}</strong><small>{item.detail}</small></span><span aria-hidden="true">→</span></Link></li>
      )}</ul>}
      {section.count > section.items.length && <p>Mostrate le prime {section.items.length} di {section.count} attività.</p>}
      {section.count > 0 && <Link className="clinic-button secondary" to={section.href}>Apri {section.href === '/invoices' ? 'fatture' : section.href === '/agenda' ? 'agenda' : 'attività'}</Link>}
    </section>)}
    <p>Riepilogo delle attività registrate in Sibilla. Gli appuntamenti e i calendari collegati si consultano in agenda.</p>
    <Link className="clinic-button secondary" to="/agenda">Consulta agenda</Link>
  </div>;
}

export default SibillaCopilot;
